// vim: set ts=2 sts=2 sw=2 et:

import { Equipment, Event, Flight, Lift, LoadedPlate } from "../../types/dataTypes";
import { JUDGE_POSITIONS, JudgePosition, JudgeVoteValue, JudgeVotes } from "../judging/resolveJudgeVotes";
import { PlatformRef } from "../types";
import type { WrpfRecordCategory, WrpfSponsor } from "../handoff/types";
import { PlatformLiveFeatures } from "./platformLiveProfile";

export const PLATFORM_LIVE_WS_PATH = "/wrpf/platform-live";

/** Identidad de sala del live server: (torneo, día, tarima). Permite N salas concurrentes. */
export const roomKeyFromParts = (tournamentId: string | undefined | null, day: number, platformId: number): string => {
  return `${tournamentId && tournamentId.length > 0 ? tournamentId : "default"}:${day}:${platformId}`;
};

export type PlatformLiveRoomQuery = {
  tournamentId?: string;
  day?: number;
  platformId?: number;
};

/** Lee (tournamentId, day, platform) del query string de la URL de conexión WS. */
export const parseRoomQuery = (search: string): PlatformLiveRoomQuery => {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const tournamentId = params.get("tournamentId") || undefined;
  const dayRaw = params.get("day");
  const platformRaw = params.get("platform");
  const day = dayRaw !== null && Number.isInteger(Number(dayRaw)) ? Number(dayRaw) : undefined;
  const platformId = platformRaw !== null && Number.isInteger(Number(platformRaw)) ? Number(platformRaw) : undefined;
  return { tournamentId, day, platformId };
};

/** Agrega el query de sala a una URL WS (tras limpiar search en bridgeUrlToWebSocketUrl). */
export const appendRoomQuery = (
  wsUrl: string,
  room: { tournamentId?: string | null; day: number; platformId: number },
): string => {
  const params = new URLSearchParams();
  if (room.tournamentId && room.tournamentId.length > 0) {
    params.set("tournamentId", room.tournamentId);
  }
  params.set("day", String(room.day));
  params.set("platform", String(room.platformId));
  const separator = wsUrl.includes("?") ? "&" : "?";
  return `${wsUrl}${separator}${params.toString()}`;
};
export interface PlatformLiveAttemptBarLoad {
  loading: Array<LoadedPlate>;
  inKg: boolean;
  rackInfo: string;
}

export interface PlatformLiveAttemptAthleteMeta {
  team: string;
  lift: Lift;
  category: string;
  age: number;
  ageDivision?: string;
  bodyweightKg?: number;
  equipment?: Equipment;
  weightClassKg?: string;
  /** OpenLifter entry events; used as Modalidad fallback when web classification is absent. */
  events?: Array<Event>;
  instagram?: string;
  photoUrl?: string;
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
  primaryClassification?: {
    event?: string;
    equipment: string;
    division?: string;
    weightClass?: string;
  };
  personalBestsKg?: {
    squat?: number;
    bench?: number;
    deadlift?: number;
  };
}

export type PlatformLiveAttemptStatus = "pending" | "good" | "noLift";

export interface PlatformLiveAttemptSeriesItem {
  weightKg: number;
  status: PlatformLiveAttemptStatus;
  /** When true, spectator attempt history shows a record badge for this attempt slot. */
  recordAttempt?: boolean;
  /** Typed live category. Missing on legacy payloads that only carry recordAttempt. */
  recordCategory?: WrpfRecordCategory;
}

/** Datos del torneo para las pantallas de transmisión; viajan con el intento actual y las pantallas los recuerdan. */
export interface PlatformLiveBroadcastInfo {
  /** Nombre del torneo: la configuración del meet (`meet.name`), que llena el bundle de la web. */
  meetName?: string;
  sponsors?: Array<WrpfSponsor>;
}

/** Intentos de un movimiento del Entry; `liftSeries` publica uno por cada movimiento de sus modalidades. */
export interface PlatformLiveLiftSeries {
  lift: Lift;
  attempts: Array<PlatformLiveAttemptSeriesItem>;
}

export interface PlatformLiveAttempt {
  key: string;
  day: number;
  platformId: number;
  entryId: number;
  lift: Lift;
  attempt: number;
  lifterName: string;
  weightKg: number;
  flight?: Flight;
  /** When true, live displays show a record-attempt badge for this attempt. */
  recordAttempt?: boolean;
  /** Typed live category. Missing on legacy payloads that only carry recordAttempt. */
  recordCategory?: WrpfRecordCategory;
  barLoad?: PlatformLiveAttemptBarLoad;
  athleteMeta?: PlatformLiveAttemptAthleteMeta;
  attemptSeries?: Array<PlatformLiveAttemptSeriesItem>;
  /**
   * Intentos de todos los movimientos del Entry (incluido el actual), para mostrar mejores y total
   * acumulado. Opcional: sin este campo las pantallas solo conocen `attemptSeries` del movimiento actual.
   */
  liftSeries?: Array<PlatformLiveLiftSeries>;
  /** Solo en el intento actual. Opcional: el live server lo reenvía tal cual. */
  broadcast?: PlatformLiveBroadcastInfo;
}

export interface PlatformLiveResolvedAttempt {
  attemptKey: string;
  attempt: PlatformLiveAttempt | null;
  source: "automatic" | "manual";
  result: JudgeVoteValue;
  resolvedAtMs?: number;
  revealUntilMs?: number;
  judgeLights?: JudgeLights;
}

export interface PlatformLiveAttemptClock {
  attemptKey: string;
  startedAtMs: number;
  endsAtMs: number;
  durationMs: number;
  /** When set, the clock is PAUSED and frozen at this many ms remaining (every viewer freezes). */
  pausedRemainingMs?: number;
}

export type DmxStatus = "disabled" | "ready" | "degraded";

export type JudgeLightValue = "off" | "white" | "red";
export type JudgeLights = Record<JudgePosition, JudgeLightValue>;

export interface PlatformLiveState {
  operatorConnected: boolean;
  serverNowMs: number;
  currentAttempt: PlatformLiveAttempt | null;
  nextAttempt: PlatformLiveAttempt | null;
  lastResolvedAttempt: PlatformLiveResolvedAttempt | null;
  attemptClock: PlatformLiveAttemptClock | null;
  judgeLights: JudgeLights;
  votes: JudgeVotes;
  closedAttemptKey: string | null;
  features: PlatformLiveFeatures;
  dmx: {
    status: DmxStatus;
  };
}

export type OperatorJoinMessage = {
  type: "operatorJoin";
  day: number;
  platformId: number;
};

export type JudgeJoinMessage = {
  type: "judgeJoin";
  day: number;
  platformId: number;
  judgePosition: JudgePosition;
};

export type SpectatorJoinMessage = {
  type: "spectatorJoin";
  day: number;
  platformId: number;
};

export type CurrentAttemptMessage = {
  type: "currentAttempt";
  day: number;
  platformId: number;
  attempt: PlatformLiveAttempt | null;
  nextAttempt?: PlatformLiveAttempt | null;
};

export type JudgeVoteMessage = {
  type: "judgeVote";
  day: number;
  platformId: number;
  attemptKey: string;
  vote: JudgeVoteValue;
};

export type BarReadyMessage = {
  type: "barReady";
  day: number;
  platformId: number;
  attemptKey: string;
};

export type StopAttemptClockMessage = {
  type: "stopAttemptClock";
  day: number;
  platformId: number;
  attemptKey: string;
};

export type PauseAttemptClockMessage = {
  type: "pauseAttemptClock";
  day: number;
  platformId: number;
  attemptKey: string;
};

export type ResumeAttemptClockMessage = {
  type: "resumeAttemptClock";
  day: number;
  platformId: number;
  attemptKey: string;
};

export type AttemptResolvedMessage = {
  type: "attemptResolved";
  day: number;
  platformId: number;
  attemptKey: string;
  source: "automatic" | "manual";
  result: JudgeVoteValue;
};

export type PlatformLiveClientMessage =
  | OperatorJoinMessage
  | JudgeJoinMessage
  | SpectatorJoinMessage
  | CurrentAttemptMessage
  | JudgeVoteMessage
  | BarReadyMessage
  | StopAttemptClockMessage
  | PauseAttemptClockMessage
  | ResumeAttemptClockMessage
  | AttemptResolvedMessage;

export type AcceptedMessage = {
  type: "accepted";
  role: "operator" | "judge" | "spectator";
  judgePosition?: JudgePosition;
  state: PlatformLiveState;
};

export type RejectedMessage = {
  type: "rejected";
  reason: string;
};

export type PlatformStateMessage = {
  type: "platformState";
  state: PlatformLiveState;
};

export type JudgeVoteRelayMessage = {
  type: "judgeVote";
  judgePosition: JudgePosition;
  attemptKey: string;
  vote: JudgeVoteValue;
};

export type AttemptClosedMessage = {
  type: "attemptClosed";
  attemptKey: string;
  source: "automatic" | "manual";
  result: JudgeVoteValue;
};

export type ReplacedMessage = {
  type: "replaced";
  reason: string;
};

export type PlatformLiveServerMessage =
  | AcceptedMessage
  | RejectedMessage
  | PlatformStateMessage
  | JudgeVoteRelayMessage
  | AttemptClosedMessage
  | ReplacedMessage;

export const getDefaultPlatformLivePort = (platformId: number): number => {
  return 4510 + platformId;
};

export const isJudgePosition = (value: string | undefined): value is JudgePosition => {
  return JUDGE_POSITIONS.indexOf(value as JudgePosition) !== -1;
};

export const makeAttemptKey = (
  platformRef: PlatformRef,
  entryId: number,
  lift: Lift,
  attemptOneIndexed: number,
): string => {
  return [platformRef.day, platformRef.platformId, entryId, lift, attemptOneIndexed].join(":");
};

export const getPlatformLiveWebSocketUrl = (
  platformRef: PlatformRef,
  locationLike: { protocol: string; hostname: string },
  search: string,
): string => {
  const params = new URLSearchParams(search);
  const override = params.get("ws");
  if (override !== null && override.length > 0) {
    return override;
  }

  const protocol = locationLike.protocol === "https:" ? "wss:" : "ws:";
  const port = getDefaultPlatformLivePort(platformRef.platformId);
  return `${protocol}//${locationLike.hostname}:${port}${PLATFORM_LIVE_WS_PATH}`;
};
